import { cn } from "@/modules/lib/utils"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { ChevronLeft } from "lucide-react"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useSubscriberInternalNoteForm } from "@/modules/programs/programSubscriber/formik/useSubscriberInternalNoteFormik"

type ProgramSubscriberInternalNoteFormProps = {
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity
  moveToSubscriberDetails: () => void
  programId: string
  className?: string
} & React.ComponentProps<"form">

export function ProgramSubscriberInternalNoteForm({
  subscriberDetails,
  moveToSubscriberDetails,
  programId,
  className,
  ...props
}: ProgramSubscriberInternalNoteFormProps) {
  const { form, isSubmitting } = useSubscriberInternalNoteForm(programId, subscriberDetails, moveToSubscriberDetails)

  return (
    <form
      className={cn("flex grow flex-col justify-between gap-5 p-2 pt-5", className)}
      onSubmit={(e) => {
        e.preventDefault()
        void form.handleSubmit()
      }}
      {...props}
    >
      {/* Form header */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex w-full flex-row justify-center">
          <Button variant="underline" onClick={moveToSubscriberDetails} className="absolute left-0">
            <ChevronLeft color="currentColor" size={13} className="text-textDefault-caption" />
            {word("global.return")}
          </Button>

          <Text fontStyle="BL" className="text-center">
            {word("program.subscribers.subscriptionDetails.internalNote.title")}
          </Text>
        </div>

        <div className="flex flex-row items-center gap-2">
          <div>
            <WarningIcon color="currentColor" width={20} height={20} className="fill-textDefault-caption" />
          </div>
          <Text fontStyle="BM" className="text-center text-textDefault-caption">
            {word("program.subscribers.subscriptionDetails.internalNote.description")}
          </Text>
        </div>
      </div>

      {/* Form */}
      <div className="flex grow">
        <form.AppField name="internalNote">
          {(field) => (
            <field.Field>
              <field.Label>{word("program.form.description.label")}</field.Label>
              <field.Content>
                <field.Textarea
                  placeholder={word("program.subscribers.subscriptionDetails.internalNote.placeholder")}
                  className="field-sizing-fixed"
                  rows={5}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>
      </div>

      {/* Submit button */}
      <div className="mt-auto flex flex-row items-center gap-6">
        <Button variant="outline" size="lg" onClick={moveToSubscriberDetails} className="flex-1">
          {word("global.cancel")}
        </Button>

        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(formIsSubmitting) => (
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              type="submit"
              disabled={isSubmitting || formIsSubmitting}
              isLoading={isSubmitting || formIsSubmitting}
            >
              {word("global.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form>
  )
}
